import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface Props {
    onDrop: (data: any) => void;
    onPastedText?: (text: string) => void;
    getDataFromClipboardEnabled?: boolean;
    clipboardImageEnabled?: boolean;
    hoverFontSize?: number;
    disabled?: boolean;
    singleFile?: boolean;
    className?: string;
    children?: React.ReactNode;
    visible?: boolean;
    acceptedTypes?: string[];
}

const DragAndDrop = ({
    visible,
    onDrop,
    onPastedText,
    getDataFromClipboardEnabled,
    clipboardImageEnabled = true,
    hoverFontSize,
    disabled,
    children,
    singleFile,
    acceptedTypes,
    ...props
}: Props) => {
    const [drag, setDrag] = useState<boolean>(false);
    const [invalidFileDragged, setInvalidFileDragged] = useState(false);
    const dropRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        if (!getDataFromClipboardEnabled) return;

        const handlePaste = (e: ClipboardEvent | any): void => {
            // Ignore paste if certain tagname.
            if (['INPUT'].includes(e?.target?.nodeName)) return;

            // No clip data found.
            if (!e?.clipboardData) return;

            if (
                e?.clipboardData?.files?.length > 0 &&
                (!clipboardImageEnabled ? !e.clipboardData.files[0].type.startsWith('image/') : true)
            ) {
                onDrop(e.clipboardData.files);
            } else if (onPastedText != null) {
                const pastedText = e?.clipboardData?.getData('Text');
                if (pastedText) onPastedText(pastedText);
            }
        };

        window.addEventListener('paste', handlePaste);

        return () => window.removeEventListener('paste', handlePaste);
    }, [getDataFromClipboardEnabled, clipboardImageEnabled, onPastedText, onDrop]);

    useEffect(() => {
        let dragCounter = 0;
        let resetInvalidTimer: NodeJS.Timeout | null = null;

        const handleDragStart = (e: DragEvent): void => {
            if (e.dataTransfer != null && e.dataTransfer.files?.length > 0) {
                e.dataTransfer.clearData();
                dragCounter = 0;
            }
        };

        const handleDrag = (e: DragEvent): void => {
            e.preventDefault();
            e.stopPropagation();
        };

        const handleDragIn = (e: DragEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter++;
            if (e.dataTransfer != null && e?.dataTransfer?.items?.length > 0) {
                setDrag(true);
            }
        };

        const handleDragOut = (e: DragEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter--;
            if (dragCounter === 0) {
                setDrag(false);
            }
        };

        const handleDrop = (e: DragEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            setDrag(false);
            dragCounter = 0;

            const files = Array.from(e.dataTransfer?.files ?? []);
            const validFiles = acceptedTypes
                ? files.filter((file) => acceptedTypes.some((type) => file.name.toLowerCase().endsWith(type)))
                : files;

            if (validFiles.length === 0) {
                setInvalidFileDragged(true);
                if (resetInvalidTimer) clearTimeout(resetInvalidTimer);
                resetInvalidTimer = setTimeout(() => setInvalidFileDragged(false), 5000);
                return;
            }
            onDrop(validFiles);
        };

        const div = dropRef?.current;
        if (div != null) {
            div.addEventListener('dragstart', handleDragStart);
            div.addEventListener('dragenter', handleDragIn);
            div.addEventListener('dragleave', handleDragOut);
            div.addEventListener('dragover', handleDrag);
            div.addEventListener('drop', handleDrop);
            return () => {
                div.removeEventListener('dragstart', handleDragStart);
                div.removeEventListener('dragenter', handleDragIn);
                div.removeEventListener('dragleave', handleDragOut);
                div.removeEventListener('dragover', handleDrag);
                div.removeEventListener('drop', handleDrop);
                if (resetInvalidTimer) clearTimeout(resetInvalidTimer);
            };
        }
    }, [onDrop, acceptedTypes]);

    return (
        <DragAndDropWrapper ref={dropRef} isDragging={!!drag} disabled={disabled} visible={visible}>
            <Overlay show={drag} fontSize={hoverFontSize} {...props}>
                {singleFile ? t('file.draggingSingleText') : t('file.draggingText')}
            </Overlay>
            {invalidFileDragged && (
                <Overlay show>{t('file.invalidFileDragged', { value: acceptedTypes?.join(', ') })}</Overlay>
            )}
            {children}
        </DragAndDropWrapper>
    );
};

export default DragAndDrop;

const DragAndDropWrapper = styled.div<{ isDragging?: boolean; disabled?: boolean; visible?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    z-index: ${({ isDragging }) => (isDragging ? 9999 : 0)};
    ${({ disabled }) => disabled && 'pointer-events: none;'};
    ${({ visible, theme }) => visible && `border: 1px dashed ${theme.colors.border}; padding: 20px;`};
`;

const Overlay = styled.div<{ show?: boolean; fontSize?: number }>`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    font-size: ${({ fontSize }) => fontSize ?? 18}px;
    text-align: center;
    padding: 15px;
    background-color: rgba(255, 255, 255, 0.95);
    transition: opacity 0.2s ease, visibility 0.2s ease;
    height: inherit;
    width: inherit;
    visibility: hidden;
    opacity: 0;

    ${({ show }) => show && 'opacity: 1; visibility: visible;'}
`;
